import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  alpha,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Hero: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const handleGetStarted = () => {
    navigate('/signup');
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '90vh', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.primary.main, 0.05)} 0%, 
          ${alpha(theme.palette.secondary.main, 0.05)} 50%,
          ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 20% 30%, ${alpha(
            theme.palette.primary.main,
            0.08
          )} 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, ${alpha(
              theme.palette.secondary.main,
              0.08
            )} 0%, transparent 50%)`,
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 6, md: 8 } }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          {/* Trust Badge */}
          <Chip
            icon={<TrendingUpIcon />}
            label="Trusted by 1000+ Agile Teams Worldwide"
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.1),
              color: theme.palette.success.dark,
              fontWeight: 600,
              fontSize: '0.875rem',
              px: 1,
              '& .MuiChip-icon': {
                color: theme.palette.success.main,
              },
            }}
          />

          {/* Main Headline with Gradient */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
              fontWeight: 900,
              lineHeight: 1.1,
              maxWidth: '900px',
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            Transform Your Project Management
          </Typography>

          {/* Subheadline */}
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
              color: theme.palette.text.secondary,
              maxWidth: '700px',
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            Modern Agile PM with <strong>AI-Powered Estimation</strong>, Real-Time
            Collaboration, and Advanced Analytics. Streamline your workflow and deliver
            faster.
          </Typography>

          {/* CTA Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mt: 4, width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleGetStarted}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.125rem',
                fontWeight: 700,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.45)}`,
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                },
              }}
            >
              Start Free Trial
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<PlayArrowIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.125rem',
                fontWeight: 600,
                borderRadius: 3,
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              }}
            >
              Watch Demo
            </Button>
          </Stack>

          {/* Trust Indicators */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 4 }}
            alignItems="center"
            sx={{ mt: 3, opacity: 0.9 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: '1.25rem' }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Free 14-day trial
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: '1.25rem' }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                No credit card required
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: '1.25rem' }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Cancel anytime
              </Typography>
            </Stack>
          </Stack>

          {/* Hero Image/Dashboard Preview */}
          <Box
            sx={{
              mt: { xs: 6, md: 8 },
              width: '100%',
              maxWidth: '1100px',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: theme.palette.background.paper,
                transition: 'all 0.5s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 30px 80px ${alpha(theme.palette.primary.main, 0.35)}`,
                },
              }}
            >
              {/* Placeholder for dashboard screenshot */}
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '16/9',
                  background: `linear-gradient(135deg, 
                    ${alpha(theme.palette.primary.light, 0.1)} 0%, 
                    ${alpha(theme.palette.secondary.light, 0.1)} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                {/* Animated background pattern */}
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `radial-gradient(circle at 2px 2px, ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )} 1px, transparent 0)`,
                    backgroundSize: '32px 32px',
                  }}
                />
                
                {/* Dashboard Preview Text */}
                <Stack spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: alpha(theme.palette.primary.main, 0.6),
                      fontWeight: 700,
                    }}
                  >
                    Dashboard Preview
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: alpha(theme.palette.text.secondary, 0.5),
                      fontStyle: 'italic',
                    }}
                  >
                    Screenshot will be added here
                  </Typography>
                </Stack>
              </Box>
            </Box>

            {/* Floating Badges */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: -20, md: -30 },
                right: { xs: -10, md: 20 },
                bgcolor: theme.palette.background.paper,
                borderRadius: 3,
                px: 2,
                py: 1.5,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <Typography variant="body2" fontWeight={700} color="primary">
                🤖 AI-Powered
              </Typography>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: -20, md: -30 },
                left: { xs: -10, md: 20 },
                bgcolor: theme.palette.background.paper,
                borderRadius: 3,
                px: 2,
                py: 1.5,
                boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.2)}`,
                border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <Typography variant="body2" fontWeight={700} color="success.dark">
                ⚡ 40% Faster Planning
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>

      {/* Decorative Elements */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '100px',
          background: `linear-gradient(to top, ${theme.palette.background.default}, transparent)`,
          zIndex: 0,
        }}
      />
    </Box>
  );
};

export default Hero;

